// src/pages/rapim/RapimDetail.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Edit, Play, Square, Presentation, Download, Plus, Trash2,
  Users, Clock, MapPin, FileText, CheckCircle, MessageSquare,
  Calendar, Target, AlertTriangle, TrendingUp, XCircle, Paperclip,
  Search, ChevronDown, ChevronRight, Eye, EyeOff, Gavel, BarChart3,
  Send, Award, Timer, Hash, Building2, Zap, Shield, Star,
  MoreHorizontal, ExternalLink, Copy, Check, X, Info,
  PlayCircle, StopCircle, Ban, Printer, Share2,
  FileImage, FileSpreadsheet, Archive, File,
} from 'lucide-react';
import {
  Button, Badge, Card, Breadcrumb, Modal, Textarea, Input, ProgressBar, Select, Loading, LogoLoadingMark,
} from '../../components/ui';
import { TipTapEditor } from '../../components/ui/TipTapEditor';
import { SortableAgendaList, AgendaItem } from '../../components/rapim';
import KpiCard from '../../components/monitoring/KpiCard';
import toast from 'react-hot-toast';
import { rapimService } from '../../services/rapim';
import { adminService } from '../../services/admin';
import { useRole } from '../../hooks/useRole';
import {
  RapatPimpinan, PpeppRekomendasi, PpeppCreatePayload,
  RapimAgendaItem, PpeppKpi, Unit,
} from '../../types';

// ========== TYPES ==========
interface AvailableLaporanItem {
  id: number;
  kode: string;
  judul: string;
  tingkat_risiko: 'rendah' | 'sedang' | 'tinggi' | 'kritis' | 'critical';
  unit?: { id: number; name: string } | null;
  rapim_ulang?: boolean;
  riwayat_ppepp?: {
    ppepp_id?: number;
    progress_terakhir?: number;
    hambatan?: string;
    alasan_kembali?: string;
  } | null;
}

// ========== CONSTANTS ==========
const STATUS_THEME = {
  dijadwalkan: {
    gradient: 'from-blue-500 to-indigo-600',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    icon: Calendar,
    label: 'Dijadwalkan',
    dot: 'bg-blue-500',
  },
  berlangsung: {
    gradient: 'from-amber-500 to-orange-600',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    icon: PlayCircle,
    label: 'Sedang Berlangsung',
    dot: 'bg-amber-500 animate-pulse',
  },
  selesai: {
    gradient: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    icon: CheckCircle,
    label: 'Selesai',
    dot: 'bg-emerald-500',
  },
  dibatalkan: {
    gradient: 'from-red-500 to-rose-600',
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    icon: Ban,
    label: 'Dibatalkan',
    dot: 'bg-red-500',
  },
} as const;

const JENIS_CONFIG = {
  rutin: { label: 'Rutin', color: 'bg-teal-500' },
  insidental: { label: 'Insidental', color: 'bg-amber-500' },
  darurat: { label: 'Darurat', color: 'bg-red-500' },
};

const PEMBAHASAN_STATUS = {
  pending: { label: 'Menunggu', bg: 'bg-gray-100', text: 'text-gray-600', icon: Clock },
  dibahas: { label: 'Sedang Dibahas', bg: 'bg-blue-100', text: 'text-blue-700', icon: MessageSquare },
  diputuskan: { label: 'Diputuskan', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
  dibatalkan: { label: 'Dibatalkan', bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
};

// ========== SUB-COMPONENTS ==========

// Hero Status Banner
const StatusHero: React.FC<{
  rapat: RapatPimpinan;
  agendaCount: number;
  laporanCount: number;
  keputusanCount: number;
  totalDuration: number;
}> = ({ rapat, agendaCount, laporanCount, keputusanCount, totalDuration }) => {
  const theme = STATUS_THEME[rapat.status as keyof typeof STATUS_THEME] || STATUS_THEME.dijadwalkan;
  const StatusIcon = theme.icon;
  const jenisConfig = JENIS_CONFIG[rapat.jenis as keyof typeof JENIS_CONFIG];

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${theme.gradient} p-6 text-white shadow-lg`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/20 blur-2xl" />
        <div className="absolute -left-10 -bottom-10 w-32 h-32 rounded-full bg-white/10 blur-xl" />
      </div>

      <div className="relative z-10">
        {/* Status Badge */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
            <span className={`w-2 h-2 rounded-full ${theme.dot}`} />
            <StatusIcon className="w-4 h-4" />
            <span className="text-sm font-bold">{theme.label}</span>
          </div>
          {jenisConfig && (
            <span className="px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full text-sm font-medium">
              {jenisConfig.label}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-2">
          {rapat.judul}
        </h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm mb-6">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {new Date(rapat.tanggal).toLocaleString('id-ID', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </span>
          {rapat.tempat && (
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" />
              {rapat.tempat}
            </span>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Peserta', value: rapat.peserta?.length || 0, icon: Users },
            { label: 'Agenda Rinci', value: agendaCount, icon: Calendar },
            { label: 'Laporan', value: laporanCount, icon: FileText },
            { label: 'Keputusan', value: keputusanCount, icon: Gavel },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl">
              <stat.icon className="w-5 h-5 text-white/70" />
              <div>
                <p className="text-2xl font-black leading-none">{stat.value}</p>
                <p className="text-xs text-white/60 font-medium">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cancellation reason */}
      {rapat.status === 'dibatalkan' && rapat.alasan_pembatalan && (
        <div className="relative z-10 mt-4 p-3 bg-white/10 backdrop-blur-sm rounded-xl">
          <p className="text-sm text-white/90">
            <span className="font-bold">Alasan:</span> {rapat.alasan_pembatalan}
          </p>
        </div>
      )}
    </div>
  );
};

// Modern Tab Navigation
const TabNavigation: React.FC<{
  activeTab: string;
  onTabChange: (tab: any) => void;
  tabs: Array<{ id: string; label: string; icon: React.ReactNode; count?: number; badge?: string }>;
}> = ({ activeTab, onTabChange, tabs }) => (
  <div className="relative">
    <div className="flex items-center gap-1 p-1.5 bg-neutral-bg rounded-2xl overflow-x-auto scrollbar-none">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`
              relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
              transition-all duration-300 whitespace-nowrap
              ${isActive
                ? 'bg-white text-teal-main shadow-md shadow-teal-main/10'
                : 'text-neutral-text-secondary hover:text-neutral-text-primary hover:bg-white/50'
              }
            `}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`
                min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold
                ${isActive ? 'bg-teal-main/10 text-teal-main' : 'bg-neutral-border/50 text-neutral-text-secondary'}
              `}>
                {tab.count}
              </span>
            )}
            {tab.badge && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-bold rounded-full uppercase">
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

// Peserta Card with modern attendance UI
const PesertaCard: React.FC<{
  peserta: any;
  onAttendance?: (userId: number, hadir: boolean) => void;
  loading?: boolean;
  canManage?: boolean;
  isCancelled?: boolean;
}> = ({ peserta, onAttendance, loading, canManage, isCancelled }) => {
  const isBelumDicatat = !peserta.pivot?.hadir && !peserta.pivot?.keterangan_absensi;
  const hadir = peserta.pivot?.hadir;

  return (
    <div className={`
      group flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200
      ${hadir ? 'bg-emerald-50/50 border-emerald-200' :
        isBelumDicatat ? 'bg-white border-neutral-border/50 hover:border-neutral-border' :
          'bg-red-50/50 border-red-200'}
    `}>
      {/* Avatar */}
      <div className={`
        w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-sm shrink-0
        ${hadir ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' :
          isBelumDicatat ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
            'bg-gradient-to-br from-red-400 to-red-500'}
      `}>
        {peserta.name?.charAt(0)?.toUpperCase()}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-neutral-text-primary truncate">{peserta.name}</p>
        <p className="text-xs text-neutral-text-secondary">
          {peserta.role?.display_name || peserta.role?.name || '-'}
        </p>
        {peserta.unit?.name && (
          <p className="text-[10px] text-neutral-text-secondary mt-0.5 flex items-center gap-1">
            <Building2 className="w-3 h-3" />
            {peserta.unit.name}
          </p>
        )}
        {peserta.pivot?.keterangan_absensi && (
          <p className="text-[10px] text-neutral-text-secondary mt-1 italic">
            "{peserta.pivot.keterangan_absensi}"
          </p>
        )}
      </div>

      {/* Status & Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Status Indicator */}
        <span className={`
          px-3 py-1.5 rounded-xl text-xs font-bold transition-all
          ${hadir ? 'bg-emerald-100 text-emerald-700' :
            isBelumDicatat ? 'bg-gray-100 text-gray-500' :
              'bg-red-100 text-red-700'}
        `} hidden aria-hidden="true">
          {hadir ? 'Hadir' : isBelumDicatat ? 'Belum' : 'Absen'}
        </span>
        <span className={`
          px-3 py-1.5 rounded-xl text-xs font-bold transition-all
          ${hadir ? 'bg-emerald-100 text-emerald-700' :
            isBelumDicatat ? 'bg-gray-100 text-gray-500' :
              'bg-red-100 text-red-700'}
        `}>
          {hadir ? 'Hadir' : isBelumDicatat ? 'Belum' : 'Absen'}
        </span>

        {/* Quick Actions */}
        {canManage && !isCancelled && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onAttendance?.(peserta.id, true)}
              disabled={loading}
              className={`p-2 rounded-xl transition-all ${hadir ? 'bg-emerald-200 text-emerald-700' : 'hover:bg-emerald-100 text-neutral-text-secondary hover:text-emerald-600'
                }`}
              title="Hadir"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={() => onAttendance?.(peserta.id, false)}
              disabled={loading}
              className={`p-2 rounded-xl transition-all ${!hadir && !isBelumDicatat ? 'bg-red-200 text-red-700' : 'hover:bg-red-100 text-neutral-text-secondary hover:text-red-600'
                }`}
              title="Tidak Hadir"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Laporan Agenda Card
const LaporanAgendaCard: React.FC<{
  laporan: any;
  onCreateKeputusan?: () => void;
  onRemove?: () => void;
  canManage?: boolean;
  isActive?: boolean;
}> = ({ laporan, onCreateKeputusan, onRemove, canManage, isActive }) => {
  const statusConfig = PEMBAHASAN_STATUS[laporan.pivot?.status_pembahasan as keyof typeof PEMBAHASAN_STATUS]
    || PEMBAHASAN_STATUS.pending;
  const StatusIcon = statusConfig.icon;

  return (
    <div className={`
      group relative overflow-hidden rounded-2xl border-2 transition-all duration-300
      ${laporan.pivot?.status_pembahasan === 'diputuskan'
        ? 'border-emerald-200 bg-gradient-to-r from-emerald-50/50 to-white'
        : 'border-neutral-border/50 bg-white hover:border-teal-main/30 hover:shadow-lg'}
    `}>
      {/* Color accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${laporan.pivot?.status_pembahasan === 'diputuskan' ? 'bg-emerald-500' :
        laporan.pivot?.status_pembahasan === 'dibahas' ? 'bg-blue-500' :
          laporan.pivot?.status_pembahasan === 'dibatalkan' ? 'bg-red-500' :
            'bg-neutral-border'
        }`} />

      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Tags Row */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2.5 py-1 rounded-lg font-bold">
                {laporan.kode}
              </span>
              <Badge variant="risk" risk={laporan.tingkat_risiko} />
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold ${statusConfig.bg} ${statusConfig.text}`}>
                <StatusIcon className="w-3 h-3" />
                {statusConfig.label}
              </span>
            </div>

            {/* Title */}
            <h3 className="text-base font-bold text-neutral-text-primary mb-1 line-clamp-2 group-hover:text-teal-main transition-colors">
              {laporan.judul}
            </h3>

            {/* Unit */}
            <p className="text-xs text-neutral-text-secondary flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {laporan.unit?.name || '-'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            {isActive && laporan.pivot?.status_pembahasan !== 'diputuskan' && canManage && (
              <button
                onClick={onCreateKeputusan}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-main to-teal-600 text-white text-xs font-bold rounded-xl hover:shadow-lg hover:shadow-teal-main/25 transition-all active:scale-95"
              >
                <Gavel className="w-3.5 h-3.5" />
                Buat Keputusan
              </button>
            )}
            {laporan.pivot?.status_pembahasan === 'diputuskan' && (
              <span className="flex items-center gap-1.5 px-3 py-2 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-xl">
                <CheckCircle className="w-3.5 h-3.5" />
                Selesai
              </span>
            )}
            {canManage && laporan.pivot?.status_pembahasan !== 'diputuskan' && (
              <button
                onClick={onRemove}
                className="p-2 rounded-xl text-neutral-text-secondary hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                title="Hapus dari rapat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Keputusan PPEPP Card - Modern Design
const KeputusanCard: React.FC<{
  keputusan: PpeppRekomendasi;
  onViewDetail?: () => void;
}> = ({ keputusan: k, onViewDetail }) => {
  const [expanded, setExpanded] = useState(false);

  const priorityConfig = {
    kritis: { bg: 'bg-red-100', text: 'text-red-700', label: 'Kritis' },
    tinggi: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Tinggi' },
    sedang: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Sedang' },
    rendah: { bg: 'bg-green-100', text: 'text-green-700', label: 'Rendah' },
  };
  const pConfig = priorityConfig[k.priority as keyof typeof priorityConfig] || priorityConfig.sedang;

  return (
    <div className="group rounded-2xl border-2 border-neutral-border/50 bg-white overflow-hidden hover:border-teal-main/20 hover:shadow-xl transition-all duration-300">
      {/* Gradient Header */}
      <div className="bg-gradient-to-r from-teal-dark via-teal-main to-teal-600 p-5 text-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <span className="text-xs font-mono text-white/70 bg-white/15 px-2 py-0.5 rounded-md">
              {k.laporan?.kode || (k as any).laporanMasalah?.kode || '-'}
            </span>
            <h3 className="text-lg font-bold mt-2 line-clamp-2">
              {k.laporan?.judul || (k as any).laporanMasalah?.judul || '-'}
            </h3>
          </div>
          <span className={`px-3 py-1 rounded-xl text-xs font-bold shrink-0 ${pConfig.bg} ${pConfig.text}`}>
            {pConfig.label}
          </span>
        </div>

        {/* Meta Strip */}
        <div className="flex items-center gap-4 mt-4 text-white/70 text-xs">
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {k.pic_user?.name || k.pic_unit?.name || '-'}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {k.target_date ? new Date(k.target_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
          </span>
          {k.budget ? (
            <span className="flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" />
              {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(k.budget)}
            </span>
          ) : null}
        </div>
      </div>

      {/* Progress Section */}
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-neutral-text-secondary uppercase tracking-wider">
            Progress Pelaksanaan
          </span>
          <span className="text-lg font-black text-teal-main">{k.progress_percentage || 0}%</span>
        </div>
        <ProgressBar value={k.progress_percentage || 0} color="teal" size="md" showLabel={false} />
      </div>

      {/* PPEPP Expandable */}
      <div className="border-t border-neutral-border/50">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-5 py-3 text-xs font-bold text-neutral-text-secondary hover:text-teal-main transition-colors"
        >
          <span className="uppercase tracking-wider">Detail PPEPP</span>
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {expanded && (
          <div className="px-5 pb-5 space-y-3 animate-in fade-in slide-in-from-top-2">
            {[
              { label: 'Program', value: k.program, color: 'border-l-teal-500' },
              { label: 'Pengendalian', value: k.pengendalian, color: 'border-l-blue-500' },
              { label: 'Evaluasi', value: k.evaluasi, color: 'border-l-amber-500' },
              { label: 'Pengembangan', value: k.pengembangan, color: 'border-l-emerald-500' },
              { label: 'Pelaporan', value: k.pelaporan, color: 'border-l-purple-500' },
            ].map((item) => (
              <div key={item.label} className={`border-l-4 ${item.color} pl-4 py-2`}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-1">
                  {item.label}
                </p>
                <p className="text-sm text-neutral-text-primary leading-relaxed">
                  {item.value || '-'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Action */}
      <div className="px-5 py-3 bg-neutral-bg/50 border-t border-neutral-border/30">
        <Link
          to={`/tindak-lanjut/${k.id}`}
          className="flex items-center justify-center gap-2 w-full py-2.5 text-xs font-bold text-teal-main hover:text-white hover:bg-teal-main rounded-xl transition-all"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Lihat Detail Tindak Lanjut
        </Link>
      </div>
    </div>
  );
};

// Attachment Card
const AttachmentCard: React.FC<{
  attachment: any;
  onDelete?: () => void;
  canManage?: boolean;
}> = ({ attachment, onDelete, canManage }) => {
  const safeFileIcon = (() => {
    const mimeType = attachment.mime_type || '';
    if (mimeType.includes('pdf')) return <FileText className="w-6 h-6 text-red-500" />;
    if (mimeType.includes('image')) return <FileImage className="w-6 h-6 text-purple-500" />;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileSpreadsheet className="w-6 h-6 text-emerald-500" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="w-6 h-6 text-blue-500" />;
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <Presentation className="w-6 h-6 text-orange-500" />;
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('compressed')) return <Archive className="w-6 h-6 text-amber-500" />;
    return <File className="w-6 h-6 text-neutral-text-secondary" />;
  })();

  return (
    <div className="group flex items-center gap-4 p-4 rounded-2xl border border-neutral-border/50 bg-white hover:border-teal-main/20 hover:shadow-md transition-all">
      <div className="w-12 h-12 rounded-xl bg-neutral-bg flex items-center justify-center shrink-0">
        {safeFileIcon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-neutral-text-primary truncate">{attachment.original_name}</p>
        <p className="hidden" aria-hidden="true">
          {(attachment.size / 1024).toFixed(1)} KB
          {attachment.uploader?.name && ` | ${attachment.uploader.name}`}
        </p>
        <p className="text-xs text-neutral-text-secondary mt-0.5">
          {(attachment.size / 1024).toFixed(1)} KB
          {attachment.uploader?.name && ` | ${attachment.uploader.name}`}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <a
          href={attachment.url || `/storage/${attachment.path}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-xl hover:bg-teal-main/10 text-neutral-text-secondary hover:text-teal-main transition-colors"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </a>
        {canManage && onDelete && (
          <button
            onClick={onDelete}
            className="p-2 rounded-xl hover:bg-red-50 text-neutral-text-secondary hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
            title="Hapus"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
    <div className="w-20 h-20 rounded-3xl bg-neutral-bg flex items-center justify-center mb-5">
      {icon}
    </div>
    <h3 className="text-lg font-bold text-neutral-text-primary mb-2">{title}</h3>
    {description && (
      <p className="text-sm text-neutral-text-secondary max-w-sm mb-5">{description}</p>
    )}
    {action}
  </div>
);

// ========== MAIN COMPONENT ==========
const RapimDetail: React.FC = () => {
  const { id } = useParams();
  const role = useRole();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [activeTab, setActiveTab] = useState<'agenda' | 'peserta' | 'laporan' | 'notulensi' | 'keputusan' | 'lampiran'>('agenda');
  const [rapat, setRapat] = useState<RapatPimpinan | null>(null);
  const [keputusan, setKeputusan] = useState<PpeppRekomendasi[]>([]);
  const [agendaItems, setAgendaItems] = useState<RapimAgendaItem[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitUsers, setUnitUsers] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [notulenText, setNotulenText] = useState('');
  const [lastSavedNotulen, setLastSavedNotulen] = useState('');
  const [attendanceLoading, setAttendanceLoading] = useState<{ userId: number; hadir: boolean } | null>(null);

  // Modal States
  const [showAddLaporan, setShowAddLaporan] = useState(false);
  const [showKeputusan, setShowKeputusan] = useState(false);
  const [selectedLaporanId, setSelectedLaporanId] = useState<number | null>(null);
  const [showAddAgenda, setShowAddAgenda] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Available Laporan for Add
  const [availableLaporan, setAvailableLaporan] = useState<AvailableLaporanItem[]>([]);
  const [selectedAvailableLaporanIds, setSelectedAvailableLaporanIds] = useState<number[]>([]);
  const [loadingAvailableLaporan, setLoadingAvailableLaporan] = useState(false);
  const [searchAvailableLaporan, setSearchAvailableLaporan] = useState('');

  // Agenda Form
  const [agendaForm, setAgendaForm] = useState({
    title: '', description: '', type: 'pembahasan' as RapimAgendaItem['type'],
    laporan_masalah_id: undefined as number | undefined, duration_minutes: 15, order: 1,
  });

  // PPEPP Form
  const [ppepp, setPpepp] = useState<Partial<PpeppCreatePayload>>({
    program: '', pengendalian: '', evaluasi: '', pengembangan: '', pelaporan: '',
    pic_unit_id: 0, pic_user_id: undefined, target_date: '', budget: 0,
  });
  const [ppeppPriority, setPpeppPriority] = useState<'rendah' | 'sedang' | 'tinggi' | 'kritis'>('sedang');

  // Excluded unit types and roles for PIC
  const EXCLUDED_UNIT_TYPES = ['UNIVERSITAS', 'FAKULTAS', 'WAREK'];
  const EXCLUDED_PIC_ROLES = ['rektor', 'dekan', 'wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4', 'lpm'];

  // ========== FETCH DATA ==========
  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [rapatRes, kepRes, agendaRes] = await Promise.all([
        rapimService.getById(Number(id)),
        rapimService.getKeputusanByRapat(Number(id)),
        rapimService.getAgendaItems(Number(id)).catch(() => ({ data: [] })),
      ]);
      const rapatData = (rapatRes as any)?.data || rapatRes;
      setRapat(rapatData);
      setNotulenText(rapatData.notulen || '');
      setLastSavedNotulen(rapatData.notulen || '');
      const kepData = (kepRes as any)?.data || kepRes;
      setKeputusan(Array.isArray(kepData) ? kepData : (kepData?.data || []));
      const agData = (agendaRes as any)?.data || agendaRes;
      setAgendaItems(Array.isArray(agData) ? agData : (agData?.data || []));
    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat detail rapat');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load units for PPEPP form
  useEffect(() => {
    const loadUnits = async () => {
      try {
        const res = await adminService.units.getAllFlat();
        const unitsData = (res as any)?.data || res;
        const allUnits = Array.isArray(unitsData) ? unitsData : (unitsData?.data || []);
        setUnits(allUnits.filter((u: any) => !EXCLUDED_UNIT_TYPES.includes((u.type || '').toUpperCase())));
      } catch (e) { console.error('Failed to load units', e); }
    };
    loadUnits();
  }, []);

  // Load users when PIC unit selected
  useEffect(() => {
    const loadUnitUsers = async () => {
      if (!ppepp.pic_unit_id || ppepp.pic_unit_id <= 0) { setUnitUsers([]); return; }
      try {
        const res = await adminService.users.getAll({ unit_id: ppepp.pic_unit_id, per_page: 100 });
        const usersData = (res as any)?.data || res;
        const users = Array.isArray(usersData) ? usersData : (usersData?.data || []);
        const filteredUsers = users.filter((u: any) => !EXCLUDED_PIC_ROLES.includes((u.role?.name || '').toLowerCase()));
        setUnitUsers(filteredUsers.map((u: any) => ({ id: u.id, name: u.name })));
      } catch (e) { setUnitUsers([]); }
    };
    loadUnitUsers();
  }, [ppepp.pic_unit_id]);

  // ========== HANDLERS ==========
  const handleStartRapat = async () => {
    if (!rapat) return;
    setProcessing(true);
    try {
      await rapimService.startRapat(rapat.id);
      toast.success('Rapat dimulai!');
      fetchData();
    } catch (e) { toast.error('Gagal memulai rapat'); }
    finally { setProcessing(false); }
  };

  const handleConfirmEndRapat = async () => {
    if (!rapat) return;
    setProcessing(true);
    try {
      await rapimService.endRapat(rapat.id);
      toast.success('Rapat selesai!');
      setShowEndModal(false);
      fetchData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal mengakhiri rapat');
    }
    finally { setProcessing(false); }
  };

  const handleCancelRapat = async () => {
    if (!rapat || !cancelReason.trim()) {
      toast.error('Alasan pembatalan wajib diisi');
      return;
    }
    setProcessing(true);
    try {
      await rapimService.cancelRapat(rapat.id, cancelReason);
      toast.success('Rapat dibatalkan');
      setShowCancelModal(false);
      setCancelReason('');
      fetchData();
    } catch (e) { toast.error('Gagal membatalkan rapat'); }
    finally { setProcessing(false); }
  };

  const handleAttendance = async (userId: number, hadir: boolean) => {
    if (!rapat) return;
    setAttendanceLoading({ userId, hadir });
    try {
      await rapimService.updateKehadiran(rapat.id, userId, hadir);
      setRapat((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          peserta: prev.peserta?.map((p: any) =>
            p.id === userId ? { ...p, pivot: { ...p.pivot, hadir, updated_at: new Date().toISOString() } } : p
          ),
        };
      });
      toast.success(hadir ? 'Peserta ditandai hadir' : 'Peserta ditandai absen');
    } catch (e) { toast.error('Gagal menyimpan kehadiran'); }
    finally { setAttendanceLoading(null); }
  };

  const canEditNotulen = role.canManageRapim;

  const handleSaveNotulen = async () => {
    if (!rapat || !canEditNotulen) return;
    setProcessing(true);
    try {
      await rapimService.saveNotulen(rapat.id, notulenText);
      setLastSavedNotulen(notulenText);
      toast.success('Notulensi berhasil disimpan');
    } catch (e) { toast.error('Gagal menyimpan notulensi'); }
    finally { setProcessing(false); }
  };

  const handleAutoSaveNotulen = useCallback(() => {
    if (!rapat || !canEditNotulen || notulenText === lastSavedNotulen) return;
    rapimService.saveNotulen(rapat.id, notulenText)
      .then(() => { setLastSavedNotulen(notulenText); })
      .catch(() => { });
  }, [rapat, notulenText, lastSavedNotulen, canEditNotulen]);

  const handleReorderAgenda = async (items: AgendaItem[]) => {
    if (!rapat) return;
    setAgendaItems(items as RapimAgendaItem[]);
    try {
      await rapimService.reorderAgenda(rapat.id, items.map((item, index) => ({ id: item.id, order: index + 1 })));
    } catch (e) { toast.error('Gagal mengubah urutan agenda'); fetchData(); }
  };

  const handleAddAgendaItem = async () => {
    if (!rapat || !agendaForm.title.trim()) {
      toast.error('Judul agenda wajib diisi');
      return;
    }
    setProcessing(true);
    try {
      await rapimService.createAgendaItem(rapat.id, {
        ...agendaForm,
        order: agendaItems.length + 1,
      });
      toast.success('Agenda berhasil ditambahkan');
      setShowAddAgenda(false);
      setAgendaForm({ title: '', description: '', type: 'pembahasan', laporan_masalah_id: undefined, duration_minutes: 15, order: 1 });
      fetchData();
    } catch (e) { toast.error('Gagal menambahkan agenda'); }
    finally { setProcessing(false); }
  };

  const handleDeleteAgendaItem = async (itemId: number) => {
    if (!rapat || !window.confirm('Hapus agenda ini?')) return;
    try {
      await rapimService.deleteAgendaItem(rapat.id, itemId);
      toast.success('Agenda dihapus');
      fetchData();
    } catch (e) { toast.error('Gagal menghapus agenda'); }
  };

  const handleOpenAddLaporan = async () => {
    if (!rapat) return;
    setShowAddLaporan(true);
    setSearchAvailableLaporan('');
    setSelectedAvailableLaporanIds([]);
    setLoadingAvailableLaporan(true);
    try {
      const res = await rapimService.getAvailableLaporan();
      const data = (res as any)?.data || res;
      const rows: AvailableLaporanItem[] = Array.isArray(data) ? data : (data?.data || []);
      const existingIds = new Set(
        ((rapat.laporan_masalah || rapat.laporan || []) as any[]).map((l: any) => Number(l.id))
      );
      setAvailableLaporan(
        rows.filter((item) => !existingIds.has(Number(item.id)))
          .sort((a, b) => Number(Boolean(b.rapim_ulang)) - Number(Boolean(a.rapim_ulang)))
      );
    } catch (e) { toast.error('Gagal memuat daftar laporan'); }
    finally { setLoadingAvailableLaporan(false); }
  };

  const handleAddSelectedLaporan = async () => {
    if (!rapat || selectedAvailableLaporanIds.length === 0) {
      toast.error('Pilih minimal satu laporan');
      return;
    }
    setProcessing(true);
    try {
      await rapimService.addLaporan(rapat.id, selectedAvailableLaporanIds);
      toast.success(`${selectedAvailableLaporanIds.length} laporan berhasil ditambahkan`);
      setShowAddLaporan(false);
      fetchData();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Gagal menambahkan laporan'); }
    finally { setProcessing(false); }
  };

  const handleRemoveLaporan = async (laporanId: number) => {
    if (!rapat || !window.confirm('Hapus laporan ini dari agenda rapat?')) return;
    setProcessing(true);
    try {
      await rapimService.removeLaporan(rapat.id, laporanId);
      toast.success('Laporan dihapus dari rapat');
      fetchData();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Gagal menghapus laporan'); }
    finally { setProcessing(false); }
  };

  const openKeputusanModal = (laporanId: number) => {
    setSelectedLaporanId(laporanId);
    const lap = (rapat?.laporan_masalah || rapat?.laporan || [])?.find((l: any) => l.id === laporanId);
    setPpepp({
      laporan_masalah_id: laporanId, program: '', pengendalian: '', evaluasi: '',
      pengembangan: '', pelaporan: '', pic_unit_id: (lap as any)?.unit_id || 0,
      pic_user_id: undefined, target_date: '', budget: 0,
    });
    setUnitUsers([]);
    setPpeppPriority('sedang');
    setShowKeputusan(true);
  };

  const handleSaveKeputusan = async () => {
    if (!rapat || !selectedLaporanId) return;
    const errors: string[] = [];
    if (!ppepp.program || ppepp.program.trim().length < 5) errors.push('Program minimal 5 karakter');
    if (!ppepp.pengendalian || ppepp.pengendalian.trim().length < 5) errors.push('Pengendalian minimal 5 karakter');
    if (!ppepp.evaluasi || ppepp.evaluasi.trim().length < 5) errors.push('Evaluasi minimal 5 karakter');
    if (!ppepp.pengembangan || ppepp.pengembangan.trim().length < 5) errors.push('Pengembangan minimal 5 karakter');
    if (!ppepp.pelaporan || ppepp.pelaporan.trim().length < 5) errors.push('Pelaporan minimal 5 karakter');
    if (!ppepp.pic_unit_id || ppepp.pic_unit_id <= 0) errors.push('Unit PIC wajib dipilih');
    if (!ppepp.pic_user_id || ppepp.pic_user_id <= 0) errors.push('PIC wajib dipilih');
    if (!ppepp.target_date) errors.push('Target tanggal wajib diisi');
    if (errors.length > 0) {
      toast.error(errors.join('\n'), { duration: 5000 });
      return;
    }
    setProcessing(true);
    try {
      await rapimService.createKeputusan({
        rapat_id: Number(id), laporan_masalah_id: selectedLaporanId,
        program: ppepp.program?.trim() || '', pengendalian: ppepp.pengendalian?.trim() || '',
        evaluasi: ppepp.evaluasi?.trim() || '', pengembangan: ppepp.pengembangan?.trim() || '',
        pelaporan: ppepp.pelaporan?.trim() || '', pic_unit_id: Number(ppepp.pic_unit_id),
        pic_user_id: Number(ppepp.pic_user_id), target_date: ppepp.target_date || '',
        budget: Number(ppepp.budget) || 0, priority: ppeppPriority,
      } as any);
      toast.success('Keputusan PPEPP berhasil disimpan');
      setShowKeputusan(false);
      await rapimService.updateStatusPembahasan(rapat!.id, selectedLaporanId, 'diputuskan');
      fetchData();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Gagal menyimpan keputusan'); }
    finally { setProcessing(false); }
  };

  const exportPDF = async (template: 'resume' | 'hasil') => {
    if (!rapat) return;
    try {
      const arrayBuffer = await rapimService.exportPDF(rapat.id, template);
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${template === 'resume' ? 'RESUME' : 'KEPUTUSAN'}-RAPIM-${rapat.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Gagal export PDF'); }
  };

  const formatRupiahInput = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // ========== COMPUTED ==========
  if (loading || !rapat) {
    return <Loading variant="page" message="Memuat detail rapat..." />;
  }

  const laporanList = rapat.laporan_masalah || rapat.laporan || [];
  const laporanBelumDiputuskan = (laporanList as any[]).filter(
    (laporan: any) => laporan?.pivot?.status_pembahasan !== 'diputuskan'
  );
  const laporanBelumDiputuskanPreview = laporanBelumDiputuskan.slice(0, 5);
  const totalDuration = agendaItems.reduce((s, a) => s + a.duration_minutes, 0);
  const canManageLaporanRapim = role.canManageRapim && ['dijadwalkan', 'berlangsung'].includes(rapat.status);
  const isActive = ['berlangsung', 'selesai'].includes(rapat.status);

  const filteredAvailableLaporan = availableLaporan.filter((item) =>
    item.kode.toLowerCase().includes(searchAvailableLaporan.toLowerCase()) ||
    item.judul.toLowerCase().includes(searchAvailableLaporan.toLowerCase())
  );

  const tabs = [
    { id: 'agenda', label: 'Agenda', icon: <Calendar className="w-4 h-4" />, count: agendaItems.length },
    { id: 'peserta', label: 'Peserta', icon: <Users className="w-4 h-4" />, count: rapat.peserta?.length || 0 },
    { id: 'laporan', label: 'Laporan', icon: <FileText className="w-4 h-4" />, count: (laporanList as any[]).length },
    { id: 'notulensi', label: 'Notulensi', icon: <MessageSquare className="w-4 h-4" />, badge: notulenText !== lastSavedNotulen ? 'unsaved' : undefined },
    { id: 'keputusan', label: 'Keputusan', icon: <Gavel className="w-4 h-4" />, count: keputusan.length },
    { id: 'lampiran', label: 'Lampiran', icon: <Paperclip className="w-4 h-4" />, count: rapat.attachments?.length || 0 },
  ];

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'RAPIM', href: '/rapim' },
        { label: 'Detail Rapat' },
      ]} />

      {/* Action Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Link to="/rapim" className="inline-flex items-center gap-1.5 text-sm text-neutral-text-secondary hover:text-teal-main transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Kembali ke Daftar
        </Link>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowGuide((value) => !value)}
            icon={<Info className="w-4 h-4" />}
          >
            {showGuide ? 'Tutup Panduan' : 'Panduan'}
          </Button>
          {rapat.status === 'dijadwalkan' && role.canManageRapim && (
            <Button variant="success" size="sm" onClick={handleStartRapat} loading={processing}
              icon={<PlayCircle className="w-4 h-4" />}>
              Mulai Rapat
            </Button>
          )}
          {(rapat.status === 'dijadwalkan' || rapat.status === 'berlangsung') && role.canManageRapim && (
            <Button variant="outline" size="sm" onClick={() => setShowCancelModal(true)}>
              Batalkan
            </Button>
          )}
          {rapat.status === 'berlangsung' && (
            <>
              <Link to={`/rapim/${id}/presentasi`}>
                <Button variant="outline" size="sm" icon={<Presentation className="w-4 h-4" />}>
                  Presentasi
                </Button>
              </Link>
              {role.canManageRapim && (
                <Button variant="danger" size="sm" onClick={() => setShowEndModal(true)} loading={processing}
                  icon={<StopCircle className="w-4 h-4" />}>
                  Akhiri Rapat
                </Button>
              )}
            </>
          )}
          {rapat.status !== 'dibatalkan' && (
            <div className="flex items-center gap-1">
              {(rapat.status === 'dijadwalkan' || rapat.status === 'berlangsung') && (
                <Button variant="ghost" size="sm" onClick={() => exportPDF('resume')}
                  icon={<Printer className="w-4 h-4" />}>
                  Resume RAPIM
                </Button>
              )}
              {rapat.status === 'selesai' && (
                <Button variant="ghost" size="sm" onClick={() => exportPDF('hasil')}
                  icon={<Download className="w-4 h-4" />}>
                  Hasil RAPIM
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Hero Banner */}
      <StatusHero
        rapat={rapat}
        agendaCount={agendaItems.length}
        laporanCount={(laporanList as any[]).length}
        keputusanCount={keputusan.length}
        totalDuration={totalDuration}
      />

      {showGuide && (
        <Card className="border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-5 flex-1">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                  <Info className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-teal-dark">Panduan Halaman Rapat Pimpinan</h3>
                  <p className="text-sm text-neutral-text-secondary mt-1">
                    Gunakan panduan ini untuk memahami fungsi setiap bagian dalam halaman RAPIM, mulai dari penyusunan agenda sampai penetapan keputusan tindak lanjut.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Ringkasan Agenda Rapat</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Bagian ini menampilkan gambaran umum rapat. Cocok dipakai untuk memberi konteks singkat kepada peserta sebelum rapat dimulai.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Agenda Rinci</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Digunakan untuk menyusun urutan pembahasan rapat satu per satu, misalnya pembukaan, pembahasan laporan, keputusan, atau informasi tambahan.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Peserta dan Kehadiran</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Tab peserta dipakai untuk melihat siapa saja yang diundang dan mencatat status kehadiran mereka selama rapat berlangsung.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Laporan yang Dibahas</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Tab laporan menampilkan daftar laporan yang masuk ke agenda RAPIM. Di sini Anda bisa meninjau laporan, menambah laporan baru, atau mengeluarkan laporan jika diperlukan.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Buat Keputusan RAPIM</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Tombol <strong>Buat Keputusan</strong> dipakai untuk menetapkan tindak lanjut resmi hasil rapat terhadap sebuah laporan. Keputusan ini menjadi dasar pekerjaan PIC setelah RAPIM selesai.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Kapan Tombol Buat Keputusan Dipakai?</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Gunakan tombol ini setelah laporan selesai dibahas dan pimpinan sudah menyepakati tindak lanjutnya. Jika keputusan belum dibuat, laporan masih dianggap menunggu hasil pembahasan RAPIM.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Isi Keputusan PPEPP</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Saat membuat keputusan, Anda perlu menentukan Program, Pengendalian, Evaluasi, Pengembangan, dan Pelaporan, sekaligus menetapkan PIC, target tanggal, prioritas, dan anggaran jika ada.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Notulensi</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Tab notulensi dipakai untuk menulis catatan resmi rapat. Catatan dapat disimpan manual, dan sistem juga membantu menyimpan otomatis agar perubahan tidak hilang.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Lampiran</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Gunakan tab lampiran untuk mengunggah dokumen pendukung rapat, seperti bahan presentasi, dokumen pembahasan, atau file keputusan yang perlu dibagikan.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Mulai, Akhiri, dan Batalkan Rapat</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    <strong>Mulai Rapat</strong> digunakan saat pembahasan dimulai, <strong>Akhiri Rapat</strong> dipakai saat seluruh agenda selesai, dan <strong>Batalkan Rapat</strong> dipakai jika rapat tidak jadi dilaksanakan.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Mode Presentasi</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Fitur presentasi digunakan untuk menampilkan laporan dalam format yang lebih fokus saat rapat berlangsung, sehingga pimpinan dapat mengikuti pembahasan dengan lebih mudah.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Resume dan Hasil RAPIM</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    <strong>Resume RAPIM</strong> digunakan sebelum atau saat rapat untuk bahan pembahasan, sedangkan <strong>Hasil RAPIM</strong> digunakan setelah rapat selesai sebagai dokumen keputusan resmi yang dapat diunduh atau dicetak.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/80 border border-blue-100">
                  <p className="text-sm font-bold text-neutral-text-primary">Alur Singkat Penggunaan</p>
                  <p className="text-xs text-neutral-text-secondary mt-1.5 leading-relaxed">
                    Susun agenda, tentukan peserta, pilih laporan yang dibahas, jalankan rapat, buat keputusan untuk tiap laporan, isi notulensi, akhiri rapat, lalu unduh hasil RAPIM.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowGuide(false)}
              className="p-2 rounded-xl hover:bg-white/70 text-neutral-text-secondary transition-colors shrink-0"
              title="Tutup panduan"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </Card>
      )}

      {/* Tab Navigation */}
      <TabNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={tabs as any}
      />

      {/* ====== TAB CONTENT ====== */}

      {/* AGENDA TAB */}
      {activeTab === 'agenda' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-bold text-teal-dark">Agenda Rapat</h3>
          </div>

          <Card className="border border-blue-100 bg-blue-50/40">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="space-y-3">
                <div>
                  <h4 className="text-base font-bold text-neutral-text-primary">Ringkasan Agenda Rapat</h4>
                </div>
                <div className="p-4 bg-white rounded-2xl border border-blue-100">
                  {rapat.agenda ? (
                    <p className="text-sm whitespace-pre-wrap text-neutral-text-primary leading-relaxed">{rapat.agenda}</p>
                  ) : (
                    <p className="text-sm text-neutral-text-secondary">
                      Belum ada ringkasan agenda rapat.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h4 className="text-base font-bold text-teal-dark">Daftar Agenda Rinci</h4>
              {totalDuration > 0 && (
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Estimasi durasi: <span className="font-bold text-teal-main">{totalDuration} menit</span>
                </p>
              )}
            </div>
            {rapat.status === 'dijadwalkan' && role.canManageRapim && (
              <Button variant="primary" size="sm" onClick={() => setShowAddAgenda(true)}
                icon={<Plus className="w-4 h-4" />}>
                Tambah Agenda Rinci
              </Button>
            )}
          </div>

          {agendaItems.length > 0 ? (
            <SortableAgendaList
              items={agendaItems.map((item) => ({
                ...item,
                status: item.status || 'pending',
                laporanMasalah: item.laporan as any,
              }))}
              onReorder={handleReorderAgenda}
              onDelete={rapat.status === 'dijadwalkan' && role.canManageRapim ? handleDeleteAgendaItem : undefined}
            />
          ) : (
            <EmptyState
              icon={<Calendar className="w-10 h-10 text-neutral-text-secondary opacity-40" />}
              title="Belum ada daftar agenda"
              description="Tambahkan agenda rinci untuk mulai menyusun jalannya rapat."
            />
          )}
        </div>
      )}

      {/* PESERTA TAB */}
      {activeTab === 'peserta' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-teal-dark">Daftar Peserta</h3>
              <p className="text-xs text-neutral-text-secondary mt-0.5">
                {rapat.peserta?.filter((p: any) => p.pivot?.hadir).length || 0} dari {rapat.peserta?.length || 0} hadir
              </p>
            </div>
          </div>

          {/* Attendance Summary Bar */}
          {rapat.peserta && rapat.peserta.length > 0 && (
            <div className="flex items-center gap-2 p-4 bg-neutral-bg rounded-2xl">
              <div className="flex-1">
                <ProgressBar
                  value={
                    rapat.peserta.length > 0
                      ? Math.round((rapat.peserta.filter((p: any) => p.pivot?.hadir).length / rapat.peserta.length) * 100)
                      : 0
                  }
                  color="success"
                  size="sm"
                  showLabel={false}
                />
              </div>
              <span className="text-sm font-bold text-neutral-text-primary shrink-0">
                {rapat.peserta.length > 0
                  ? Math.round((rapat.peserta.filter((p: any) => p.pivot?.hadir).length / rapat.peserta.length) * 100)
                  : 0}%
              </span>
            </div>
          )}

          <div className="space-y-2">
            {rapat.peserta?.map((p: any, i: number) => (
              <PesertaCard
                key={p.id || i}
                peserta={p}
                onAttendance={handleAttendance}
                loading={attendanceLoading?.userId === p.id}
                canManage={role.canManageRapim}
                isCancelled={rapat.status === 'dibatalkan'}
              />
            ))}
          </div>
        </div>
      )}

      {/* LAPORAN TAB */}
      {activeTab === 'laporan' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-teal-dark">Laporan yang Dibahas</h3>
            {canManageLaporanRapim && (
              <Button variant="primary" size="sm" onClick={handleOpenAddLaporan}
                icon={<Plus className="w-4 h-4" />}>
                Pilih Laporan
              </Button>
            )}
          </div>

          {(laporanList as any[]).length === 0 ? (
            <EmptyState
              icon={<FileText className="w-10 h-10 text-neutral-text-secondary opacity-40" />}
              title="Belum ada laporan"
              description="Tambahkan laporan yang siap dibahas dalam rapat ini."
            />
          ) : (
            <div className="space-y-3">
              {(laporanList as any[]).map((lap: any, i: number) => (
                <LaporanAgendaCard
                  key={lap.id || i}
                  laporan={lap}
                  onCreateKeputusan={() => openKeputusanModal(lap.id)}
                  onRemove={() => handleRemoveLaporan(lap.id)}
                  canManage={role.canManageRapim}
                  isActive={isActive}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* NOTULENSI TAB */}
      {activeTab === 'notulensi' && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-teal-dark">Notulensi Rapat</h3>
              <p className="text-xs text-neutral-text-secondary mt-0.5">
                Simpan otomatis setiap 60 detik
                {notulenText !== lastSavedNotulen && (
                  <span className="ml-2 text-amber-600 font-semibold">| Ada catatan yang belum disimpan</span>
                )}
              </p>
            </div>
            {canEditNotulen && (
              <Button variant="primary" size="sm" onClick={handleSaveNotulen} loading={processing}
                icon={<Check className="w-4 h-4" />}>
                Simpan
              </Button>
            )}
          </div>

          <TipTapEditor
            content={notulenText}
            onChange={setNotulenText}
            placeholder="Tulis notulensi rapat di sini..."
            minHeight="400px"
            editable={rapat.status !== 'dibatalkan' && canEditNotulen}
            autoSaveInterval={60000}
            onAutoSave={handleAutoSaveNotulen}
          />
        </Card>
      )}

      {/* KEPUTUSAN TAB */}
      {activeTab === 'keputusan' && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-teal-dark">Keputusan RAPIM dan Tindak Lanjut (PPEPP)</h3>

          {keputusan.length === 0 ? (
            <EmptyState
              icon={<Gavel className="w-10 h-10 text-neutral-text-secondary opacity-40" />}
              title="Belum ada keputusan"
              description="Gunakan tab Laporan untuk membuat keputusan tindak lanjut bagi setiap laporan yang sudah dibahas."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {keputusan.map((k) => (
                <KeputusanCard key={k.id} keputusan={k} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* LAMPIRAN TAB */}
      {activeTab === 'lampiran' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-teal-dark">Lampiran Rapat</h3>
            {role.canManageRapim && rapat.status !== 'dibatalkan' && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    try {
                      setProcessing(true);
                      for (const file of Array.from(files)) {
                        await rapimService.uploadAttachment(Number(id), file);
                      }
                      toast.success(`${files.length} file berhasil diunggah`);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      const res = await rapimService.getById(Number(id));
                      setRapat((res as any).data || res);
                    } catch (err: any) { toast.error(err?.response?.data?.message || 'Gagal mengunggah file'); }
                    finally { setProcessing(false); }
                  }}
                  className="hidden"
                />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                  disabled={processing} icon={<Plus className="w-4 h-4" />}>
                  Tambah Lampiran
                </Button>
              </div>
            )}
          </div>

          {rapat.attachments && rapat.attachments.length > 0 ? (
            <div className="space-y-2">
              {rapat.attachments.map((att: any) => (
                <AttachmentCard
                  key={att.id}
                  attachment={att}
                  canManage={role.canManageRapim}
                  onDelete={async () => {
                    if (!window.confirm('Hapus file ini?')) return;
                    try {
                      await rapimService.deleteAttachment(rapat.id, att.id);
                      toast.success('File dihapus');
                      fetchData();
                    } catch { toast.error('Gagal menghapus file'); }
                  }}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Paperclip className="w-10 h-10 text-neutral-text-secondary opacity-40" />}
              title="Belum ada dokumen"
              description="Belum ada dokumen pendukung rapat."
            />
          )}
        </div>
      )}

      {/* ====== MODALS ====== */}

      {/* Add Laporan Modal */}
      <Modal
        isOpen={showAddLaporan}
        onClose={() => !processing && setShowAddLaporan(false)}
        title="Pilih Laporan untuk Dibahas"
        size="xl"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddLaporan(false)} disabled={processing}>Batal</Button>
            <Button variant="primary" onClick={handleAddSelectedLaporan} loading={processing}>
              Tambahkan ke Rapat ({selectedAvailableLaporanIds.length})
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            placeholder="Cari laporan (kode / judul)..."
            value={searchAvailableLaporan}
            onChange={(e) => setSearchAvailableLaporan(e.target.value)}
            icon={<Search className="w-4 h-4" />}
          />
          {loadingAvailableLaporan ? (
            <div className="flex items-center justify-center py-10">
              <LogoLoadingMark size="md" />
            </div>
          ) : filteredAvailableLaporan.length === 0 ? (
            <EmptyState
              icon={<FileText className="w-8 h-8 text-neutral-text-secondary opacity-40" />}
              title="Belum ada laporan yang siap dipilih"
            />
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
              {filteredAvailableLaporan.map((item) => {
                const checked = selectedAvailableLaporanIds.includes(item.id);
                return (
                  <label key={item.id} className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${checked ? 'bg-teal-main/5 border-teal-main/30' :
                    item.rapim_ulang ? 'bg-amber-50 border-amber-200' :
                      'bg-white border-neutral-border/50 hover:border-teal-main/20'
                    }`}>
                    <input type="checkbox" className="mt-1 rounded" checked={checked}
                      onChange={() => setSelectedAvailableLaporanIds((prev) =>
                        prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
                      )} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-mono text-teal-main font-bold">{item.kode}</span>
                        {item.rapim_ulang && (
                          <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full uppercase">RAPIM Ulang</span>
                        )}
                        <Badge variant="risk" risk={item.tingkat_risiko === 'critical' ? 'kritis' : item.tingkat_risiko as any} />
                      </div>
                      <p className="text-sm font-semibold">{item.judul}</p>
                      <p className="text-xs text-neutral-text-secondary">{item.unit?.name || '-'}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      {/* Add Agenda Modal */}
      <Modal
        isOpen={showAddAgenda}
        onClose={() => setShowAddAgenda(false)}
        title="Tambah Agenda Rinci"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddAgenda(false)}>Batal</Button>
            <Button variant="primary" onClick={handleAddAgendaItem} loading={processing}>Tambah</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl">
            <p className="text-sm font-bold text-blue-800">Gunakan form ini untuk menambah urutan pembahasan rapat</p>
            <p className="text-sm text-blue-700 mt-1 leading-relaxed">
              Isi bagian ini jika Anda ingin menyusun jalannya rapat satu per satu. Jika hanya perlu gambaran umum rapat, cukup gunakan ringkasan agenda rapat.
            </p>
          </div>
          <Input label="Judul Agenda" placeholder="Contoh: Pembahasan laporan prioritas" value={agendaForm.title}
            onChange={(e: any) => setAgendaForm({ ...agendaForm, title: e.target.value })} required />
          <Textarea label="Deskripsi (opsional)" placeholder="Tambahkan catatan singkat agar peserta memahami fokus pembahasan..." value={agendaForm.description}
            onChange={(e) => setAgendaForm({ ...agendaForm, description: e.target.value })} rows={3} />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Tipe" options={[
              { value: 'pembahasan', label: 'Pembahasan' },
              { value: 'keputusan', label: 'Keputusan' },
              { value: 'informasi', label: 'Informasi' },
            ]} value={agendaForm.type} onChange={(v) => setAgendaForm({ ...agendaForm, type: v as any })} />
            <Input label="Durasi (menit)" type="number" value={agendaForm.duration_minutes.toString()}
              onChange={(e: any) => setAgendaForm({ ...agendaForm, duration_minutes: Number(e.target.value) })} min="5" />
          </div>
        </div>
      </Modal>

      {/* Keputusan PPEPP Modal */}
      <Modal
        isOpen={showKeputusan}
        onClose={() => setShowKeputusan(false)}
        title="Buat Keputusan Tindak Lanjut RAPIM"
        size="2xl"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowKeputusan(false)}>Batal</Button>
            <Button variant="primary" onClick={handleSaveKeputusan} loading={processing}
              icon={<CheckCircle className="w-4 h-4" />}
              disabled={!ppepp.pic_unit_id || !ppepp.target_date || !ppepp.program || !ppepp.pic_user_id}>
              Simpan Keputusan
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* Validation Warning */}
          {(!ppepp.pic_unit_id || !ppepp.target_date || !ppepp.pic_user_id) && (
            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-2xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800">Data Wajib Belum Lengkap</p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-700">
                    {!ppepp.pic_unit_id && <li>- Unit PIC wajib dipilih</li>}
                    {!ppepp.pic_user_id && <li>- Penanggung Jawab wajib dipilih</li>}
                    {!ppepp.target_date && <li>- Target tanggal wajib diisi</li>}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Priority + Unit */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Prioritas" options={[
              { value: 'rendah', label: 'Rendah' }, { value: 'sedang', label: 'Sedang' },
              { value: 'tinggi', label: 'Tinggi' }, { value: 'kritis', label: 'Kritis' },
            ]} value={ppeppPriority} onChange={(v) => setPpeppPriority(v as any)} />
            <Select label="Unit PIC *" placeholder="Pilih unit..." required
              options={units.map((u) => ({ value: u.id, label: u.name }))}
              value={ppepp.pic_unit_id?.toString() || ''}
              onChange={(v) => setPpepp({ ...ppepp, pic_unit_id: v ? parseInt(v) : 0, pic_user_id: undefined })} />
          </div>

          {/* PIC User */}
          {ppepp.pic_unit_id && ppepp.pic_unit_id > 0 && (
            <Select label="Penanggung Jawab (PIC) *" placeholder="Pilih PIC..." required
              options={unitUsers.map((u) => ({ value: u.id, label: u.name }))}
              value={ppepp.pic_user_id?.toString() || ''}
              onChange={(v) => setPpepp({ ...ppepp, pic_user_id: v ? parseInt(v) : undefined })} />
          )}

          {/* PPEPP Fields */}
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-blue-800 mb-1">Panduan Pengisian PPEPP</p>
                <p className="text-xs text-blue-700">
                  PPEPP adalah kerangka tindak lanjut yang terdiri dari lima bagian utama. Isi setiap bagian dengan jelas agar keputusan rapat mudah dipahami dan dapat langsung dijalankan oleh PIC.
                </p>
              </div>
            </div>

            {[
              { 
                key: 'program', 
                label: 'Program', 
                color: 'border-l-teal-main',
                bgTip: 'bg-teal-50 border-teal-200',
                textTip: 'text-teal-700',
                placeholder: 'Tuliskan program kerja yang akan dilaksanakan...',
                tip: 'Jelaskan PROGRAM atau rencana aksi konkret yang harus dilakukan PIC untuk menyelesaikan masalah ini. Contoh: "Pengadaan 20 unit komputer baru untuk laboratorium Prodi TI pada semester ini."',
              },
              { 
                key: 'pengendalian', 
                label: 'Pengendalian', 
                color: 'border-l-blue-500',
                bgTip: 'bg-blue-50 border-blue-200',
                textTip: 'text-blue-700',
                placeholder: 'Tuliskan mekanisme pengendalian pelaksanaan...',
                tip: 'Jelaskan bagaimana pelaksanaan program akan DIKENDALIKAN agar berjalan sesuai rencana. Contoh: "Monitoring mingguan oleh Kaprodi, laporan progress setiap 2 minggu ke Dekan."',
              },
              { 
                key: 'evaluasi', 
                label: 'Evaluasi', 
                color: 'border-l-amber-500',
                bgTip: 'bg-amber-50 border-amber-200',
                textTip: 'text-amber-700',
                placeholder: 'Tuliskan metode evaluasi keberhasilan...',
                tip: 'Jelaskan bagaimana keberhasilan program akan DIEVALUASI dan diukur. Contoh: "Evaluasi melalui survei kepuasan mahasiswa dan pengecekan fisik peralatan setelah instalasi selesai."',
              },
              { 
                key: 'pengembangan', 
                label: 'Pengembangan', 
                color: 'border-l-emerald-500',
                bgTip: 'bg-emerald-50 border-emerald-200',
                textTip: 'text-emerald-700',
                placeholder: 'Tuliskan rencana pengembangan lanjutan...',
                tip: 'Jelaskan rencana PENGEMBANGAN atau peningkatan berkelanjutan setelah program selesai. Contoh: "Pelatihan penggunaan peralatan baru bagi dosen dan laboran, serta penyusunan SOP pemeliharaan berkala."',
              },
              { 
                key: 'pelaporan', 
                label: 'Pelaporan', 
                color: 'border-l-purple-500',
                bgTip: 'bg-purple-50 border-purple-200',
                textTip: 'text-purple-700',
                placeholder: 'Tuliskan mekanisme pelaporan progress...',
                tip: 'Jelaskan bagaimana progress dan hasil akhir akan DILAPORKAN. Contoh: "PIC wajib update progress melalui sistem SIM-EVAL setiap minggu, dengan bukti foto dan dokumen pendukung."',
              },
            ].map((field) => (
              <div key={field.key} className={`border-l-4 ${field.color} pl-4 py-1`}>
                <Textarea
                  label={`${field.label} *`}
                  placeholder={field.placeholder}
                  value={(ppepp as any)[field.key] || ''}
                  onChange={(e) => setPpepp({ ...ppepp, [field.key]: e.target.value })}
                  required rows={3}
                />
                {/* Tips di bawah setiap field */}
                <div className={`mt-1.5 p-2.5 rounded-lg border ${field.bgTip}`}>
                  <p className={`text-[11px] leading-relaxed ${field.textTip}`}>
                    <span className="font-bold">Panduan:</span>{' '}
                    {field.tip}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Target & Budget */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-neutral-border">
            <Input label="Target Tanggal *" type="date" required
              value={ppepp.target_date || ''}
              onChange={(e) => setPpepp({ ...ppepp, target_date: e.target.value })}
              min={new Date().toISOString().split('T')[0]} />
            <Input label="Anggaran (Rp)" type="text" inputMode="numeric" placeholder="0"
              value={formatRupiahInput(ppepp.budget ? ppepp.budget.toString() : '')}
              onChange={(e: any) => {
                const digits = e.target.value.replace(/\D/g, '');
                setPpepp({ ...ppepp, budget: digits ? parseInt(digits) : 0 });
              }} />
          </div>
        </div>
      </Modal>

      {/* End Rapat Modal */}
      <Modal
        isOpen={showEndModal}
        onClose={() => !processing && setShowEndModal(false)}
        title="Akhiri Rapat"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowEndModal(false)} disabled={processing}>Kembali</Button>
            {laporanBelumDiputuskan.length > 0 && (
              <Button
                variant="primary"
                onClick={() => {
                  setActiveTab('laporan');
                  setShowEndModal(false);
                }}
                disabled={processing}
              >
                Buka Tab Laporan
              </Button>
            )}
            <Button
              variant="danger"
              onClick={handleConfirmEndRapat}
              loading={processing}
              disabled={laporanBelumDiputuskan.length > 0}
            >
              {laporanBelumDiputuskan.length > 0 ? 'Belum Bisa Diakhiri' : 'Ya, Akhiri Rapat'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {laporanBelumDiputuskan.length > 0 ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                <p className="text-sm font-semibold text-red-800">Rapat belum dapat diakhiri</p>
                <p className="text-sm text-red-700 mt-1">
                  Masih ada {laporanBelumDiputuskan.length} laporan yang belum dibuatkan keputusan PPEPP.
                  Selesaikan keputusan untuk seluruh laporan agar tidak kembali muncul di antrean RAPIM sekretaris.
                </p>
              </div>

              <div className="rounded-2xl border border-neutral-border bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-neutral-border bg-neutral-bg">
                  <p className="text-sm font-bold text-neutral-text-primary">Laporan yang masih menunggu keputusan</p>
                  <p className="text-xs text-neutral-text-secondary mt-1">
                    Buka tab <strong>Laporan</strong>, lalu gunakan tombol <strong>Buat Keputusan</strong> pada setiap laporan berikut.
                  </p>
                </div>
                <div className="divide-y divide-neutral-border">
                  {laporanBelumDiputuskanPreview.map((laporan: any) => (
                    <div key={laporan.id} className="px-4 py-3">
                      <p className="text-xs font-mono text-teal-main">{laporan.kode}</p>
                      <p className="text-sm font-semibold text-neutral-text-primary mt-1">{laporan.judul}</p>
                    </div>
                  ))}
                  {laporanBelumDiputuskan.length > laporanBelumDiputuskanPreview.length && (
                    <div className="px-4 py-3 text-xs text-neutral-text-secondary">
                      +{laporanBelumDiputuskan.length - laporanBelumDiputuskanPreview.length} laporan lainnya masih menunggu keputusan.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl">
              <p className="text-sm font-semibold text-amber-800">Apakah Anda yakin?</p>
              <p className="text-sm text-amber-700 mt-1">Rapat akan masuk status selesai dan tidak bisa dilanjutkan.</p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Agenda Rinci', value: agendaItems.length },
              { label: 'Laporan', value: (laporanList as any[]).length },
              { label: 'Keputusan', value: keputusan.length },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-neutral-border bg-neutral-bg px-3 py-2 text-center">
                <p className="text-sm font-bold">{s.value}</p>
                <p className="text-[10px] uppercase font-semibold text-neutral-text-secondary">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Cancel Rapat Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => { setShowCancelModal(false); setCancelReason(''); }}
        title="Batalkan Rapat"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => { setShowCancelModal(false); setCancelReason(''); }}>Batal</Button>
            <Button variant="danger" onClick={handleCancelRapat} loading={processing} disabled={!cancelReason.trim()}>
              Batalkan Rapat
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
            <p className="text-sm text-red-700">
              <strong>Peringatan:</strong> Tindakan ini tidak dapat dibatalkan. Laporan yang belum dibahas akan dikembalikan statusnya.
            </p>
          </div>
          <Textarea
            label="Alasan Pembatalan"
            placeholder="Jelaskan alasan pembatalan rapat..."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            required rows={4}
          />
        </div>
      </Modal>
    </div>
  );
};

export default RapimDetail;


